import React, { useCallback } from "react";
import useFormWithSchema from "../../hooks/useFormWithSchema";
import { Controller } from "react-hook-form";
import TextInput from "../inputs/TextInput";
import { Button } from "../button";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import ComboboxInput from "../inputs/ComboboxInput";
import {
  PROCESSES,
  utilitiesConfigProcessOptions,
} from "../../constants/common";
import { useDecrypt, useEncrypt } from "../../hooks/common/mutation";
import toast from "react-hot-toast";

export const UtilitiesConfigCardSchema = Yup.object({
  id: Yup.string().required("ID is required"),
  process: Yup.string().required("Process is required"),
  value: Yup.string(),
});

interface FormValues {
  id: string;
  process: string;
  value?: string;
}

const UtilitiesConfigCard = () => {
  const { t } = useTranslation();

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useFormWithSchema(UtilitiesConfigCardSchema);

  const { mutateAsync: Encrypt } = useEncrypt();
  const { mutateAsync: Decrypt } = useDecrypt();

  const onSubmit = useCallback(
    async (data: FormValues) => {
      const { id, process } = data;

      const params = {
        value: id,
      };

      try {
        if (process === PROCESSES.ENCRYPT) {
          const res = await Encrypt(params);
          setValue("value", res);
        } else {
          const res = await Decrypt(params);
          setValue("value", res);
        }
      } catch (error) {
        toast.error(t("somethingWentWrong"));
        console.error("Error encrypting/decrypting:", error);
      }
    },
    [Decrypt, Encrypt, setValue, t]
  );

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="bg-white p-4 rounded-xl shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)] flex flex-col  gap-3 lg:gap-6"
    >
      <h2 className="text-lg lg:text-xl font-medium">Utilities</h2>
      <div className="grid md:grid-cols-3 gap-3 lg:gap-6">
        <Controller
          name="id"
          control={control}
          render={({ field: { onChange, name, value } }) => (
            <TextInput
              label="ID"
              onChange={onChange}
              value={value || ""}
              error={errors?.[name]?.message}
              required
            />
          )}
        />

        <Controller
          name="process"
          control={control}
          render={({ field: { onChange, name, value } }) => (
            <ComboboxInput
              options={utilitiesConfigProcessOptions}
              value={
                utilitiesConfigProcessOptions?.find(
                  (item) => item.value === value
                )?.labelEn || ""
              }
              onChange={onChange}
              error={errors?.[name]?.message}
              label={t("Process")}
              required
            />
          )}
        />

        <Controller
          name="value"
          control={control}
          render={({ field: { onChange, name, value } }) => (
            <TextInput
              label="Value"
              onChange={onChange}
              value={value || ""}
              error={errors?.[name]?.message}
              readOnly
            />
          )}
        />
        <div className="flex items-center gap-2">
          <Button>{t("Process")}</Button>
        </div>
      </div>
    </form>
  );
};

export default React.memo(UtilitiesConfigCard);
